import { useState } from 'react';
import moment, { Moment } from 'moment';
import { Form } from 'antd';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useBeraJobStepsContext } from '@/components/views/BeraJob/context';
import type { BeraFileFilters } from '@/components/views/BeraJob/context/types';
import { FILES_FILTER_FIELD, FIRST_PAGE } from './constants';

const { useFormInstance } = Form;

const JOB_COMPANY_FIELD = 'company_id';

type UploadDateRange = [Moment, Moment];

type FilterFormValues = {
	organization_id?: string;
	company_id?: string;
	sector_id?: string;
	line_id?: string;
	workstation_id?: string;
	upload_date?: UploadDateRange;
};

type BuildFormValuesParams = {
	filters: BeraFileFilters;
	organization_id?: string;
	company_id?: string;
};

function buildUploadDateRange({ created_at_start, created_at_end }: BeraFileFilters): UploadDateRange | undefined {
	if (!created_at_start || !created_at_end) {
		return undefined;
	}

	const result: UploadDateRange = [moment(created_at_start), moment(created_at_end)];

	return result;
}

function buildFormValues({ filters, organization_id, company_id }: BuildFormValuesParams): FilterFormValues {
	const { sector_id, line_id, workstation_id } = filters;
	const upload_date = buildUploadDateRange(filters);
	const result: FilterFormValues = { organization_id, company_id, sector_id, line_id, workstation_id, upload_date };

	return result;
}

function buildFileFilters(values: FilterFormValues = {}): BeraFileFilters {
	const { sector_id, line_id, workstation_id, upload_date } = values;
	const result: BeraFileFilters = { sector_id, line_id, workstation_id };

	if (!upload_date) {
		return result;
	}

	const [start, end] = upload_date;
	const startOfDay = moment(start).startOf('day');
	const endOfDay = moment(end).endOf('day');

	result.created_at_start = startOfDay.toISOString();
	result.created_at_end = endOfDay.toISOString();

	return result;
}

export function useFileFilters() {
	const form = useFormInstance();
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const { company, organization } = useApplicationContext();
	const { fileName, fileFilters, handlePageChange, handleFileFiltersChange } = useBeraJobStepsContext();

	function applyFilters(filters: BeraFileFilters): void {
		handleFileFiltersChange(filters);
		handlePageChange(FIRST_PAGE);
		setIsDrawerOpen(false);
	}

	function handleOpenDrawer(): void {
		const jobCompanyId: string | undefined = form.getFieldValue(JOB_COMPANY_FIELD);
		const formValues = buildFormValues({
			filters: fileFilters,
			organization_id: organization?.id,
			company_id: jobCompanyId ?? company?.id
		});

		form.setFieldValue(FILES_FILTER_FIELD, formValues);
		setIsDrawerOpen(true);
	}

	function handleCloseDrawer(): void {
		setIsDrawerOpen(false);
	}

	function handleClearFilters(): void {
		const { organization_id, company_id }: FilterFormValues = form.getFieldValue(FILES_FILTER_FIELD);
		const lockedValues: FilterFormValues = { organization_id, company_id };

		form.setFieldValue(FILES_FILTER_FIELD, lockedValues);
		applyFilters({});
	}

	function handleSubmitFilters(): void {
		const formValues: FilterFormValues | undefined = form.getFieldValue(FILES_FILTER_FIELD);
		const filters = buildFileFilters(formValues);

		applyFilters(filters);
	}

	return {
		fileName,
		isDrawerOpen,
		handleOpenDrawer,
		handleCloseDrawer,
		handleClearFilters,
		handleSubmitFilters
	};
}
